import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  ArrowRight,
  Globe2,
  Users,
  Building2,
  Landmark,
  Sparkles,
  Leaf,
  ShieldCheck,
  TrendingUp,
  Calendar,
  Mail,
  MessageSquare,
  Newspaper,
  Mic,
  FileText,
  PlayCircle,
  Award,
  Compass,
  ChevronRight,
  ChevronDown,
  MapPin,
  Linkedin,
  CheckCircle2,
  X,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------- DATA ----------
const HERO_IMG =
  "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?q=80&w=2070&auto=format&fit=crop";

const MEMBERS = [
  { name: "Singapore Tourism Board", country: "Singapore", coords: [1.3521, 103.8198], type: "Government" },
  { name: "Visit Dubai", country: "UAE", coords: [25.276987, 55.296249], type: "Government" },
  { name: "Visit Britain", country: "United Kingdom", coords: [51.509, -0.118], type: "Government" },
  { name: "Brand USA", country: "United States", coords: [38.9072, -77.0369], type: "Government" },
  { name: "Japan National Tourism Organization", country: "Japan", coords: [35.6762, 139.6503], type: "Government" },
  { name: "Marriott International", country: "USA", coords: [39.0997, -94.5786], type: "Hospitality" },
  { name: "Accor Group", country: "France", coords: [48.8566, 2.3522], type: "Hospitality" },
  { name: "Mandarin Oriental Group", country: "Hong Kong", coords: [22.3193, 114.1694], type: "Hospitality" },
  { name: "Four Seasons", country: "Canada", coords: [43.65107, -79.347015], type: "Hospitality" },
  { name: "Qatar Tourism", country: "Qatar", coords: [25.276987, 51.520008], type: "Government" },
  { name: "Saudi Tourism Authority", country: "Saudi Arabia", coords: [24.7136, 46.6753], type: "Government" },
  { name: "Tourism Australia", country: "Australia", coords: [-33.8688, 151.2093], type: "Government" },
  { name: "Tourism New Zealand", country: "New Zealand", coords: [-41.2865, 174.7762], type: "Government" },
  { name: "South African Tourism", country: "South Africa", coords: [-33.9249, 18.4241], type: "Government" },
  { name: "EMBRATUR", country: "Brazil", coords: [-15.7942, -47.8822], type: "Government" },
  { name: "Maldives Marketing & PR Corp", country: "Maldives", coords: [3.2028, 73.2207], type: "Government" },
  { name: "Maybourne Hotel Group", country: "United Kingdom", coords: [51.4892, -0.1532], type: "Hospitality" },
  { name: "Aman Resorts", country: "Switzerland", coords: [46.948, 7.4474], type: "Hospitality" },
];

const TYPE_COLORS = {
  Hospitality: "#D4AF37",
  Government: "#9CA3AF",
  Tourism: "#C9A861",
  Innovation: "#8B7355",
};

const IMPACT_METRICS = [
  { value: 64, suffix: "+", label: "Member countries" },
  { value: 280, suffix: "+", label: "Member organizations" },
  { value: 142, suffix: "M", label: "Travelers impacted" },
  { value: 47, suffix: "", label: "Active sustainability initiatives" },
  { value: 1180, suffix: "+", label: "Industry partnerships" },
];

const TIMELINE = [
  {
    phase: "Foundation",
    year: "2024",
    title: "A shared standard, born of necessity.",
    body:
      "Established by an international coalition of hospitality and government leaders to address fragmented quality standards across global travel.",
  },
  {
    phase: "Growth",
    year: "2025",
    title: "Expansion across continents.",
    body:
      "Reached 60+ member countries and onboarded the world's leading hospitality groups, DMOs, and tourism boards as founding signatories.",
  },
  {
    phase: "Transformation",
    year: "2026",
    title: "Innovation, sustainability, quality.",
    body:
      "Launched the WTTQ certification framework — uniting evidence-based assessment with sustainability benchmarks and digital transformation.",
  },
  {
    phase: "Future Vision",
    year: "2030",
    title: "The global trust standard.",
    body:
      "Becoming the definitive quality and sustainability authority across the trillion-dollar travel and tourism economy.",
  },
];

const LEADERSHIP = [
  {
    name: "H.E. Ambassador L. Renard",
    role: "Founding Chair · Alliance Council",
    org: "WTTQ.org",
    region: "Europe",
    img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=400&auto=format&fit=crop",
  },
  {
    name: "Ana Beatriz Cardoso",
    role: "Vice-Chair · Government Affairs",
    org: "Ministry of Tourism, Brazil",
    region: "Americas",
    img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=400&auto=format&fit=crop",
  },
  {
    name: "Dr. Khalid Al-Mansoori",
    role: "Council Member · Hospitality",
    org: "Gulf Hospitality Federation",
    region: "Middle East",
    img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=400&auto=format&fit=crop",
  },
  {
    name: "Yuki Tanaka",
    role: "Innovation Advisor",
    org: "Japan National Tourism Org.",
    region: "Asia Pacific",
    img: "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=400&auto=format&fit=crop",
  },
  {
    name: "Sir James Whitman",
    role: "Strategic Advisor",
    org: "International Hospitality Council",
    region: "Europe",
    img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=400&auto=format&fit=crop",
  },
  {
    name: "Olamide Adebayo",
    role: "Sustainability Lead",
    org: "African Tourism Coalition",
    region: "Africa",
    img: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?q=80&w=400&auto=format&fit=crop",
  },
];

const ECOSYSTEM = [
  {
    icon: Building2,
    title: "Hospitality",
    desc: "Hotels, resorts, airlines, cruise lines",
    examples: ["Marriott", "Accor", "Mandarin Oriental", "Four Seasons", "Aman", "Emirates"],
  },
  {
    icon: MapPin,
    title: "Tourism",
    desc: "Destinations, DMOs, attractions",
    examples: ["Visit Dubai", "Visit Britain", "Tourism Australia", "EMBRATUR", "Maldives MMPRC"],
  },
  {
    icon: Landmark,
    title: "Government",
    desc: "Ministries, tourism boards",
    examples: ["Singapore Tourism", "Qatar Tourism", "Saudi Tourism Authority", "Brand USA"],
  },
  {
    icon: Sparkles,
    title: "Innovation",
    desc: "Technology & sustainability partners",
    examples: ["Smart Destinations", "ESG Frameworks", "AI-in-Tourism Lab", "GreenTech Hospitality"],
  },
];

const FRAMEWORK = [
  { icon: ShieldCheck, title: "Quality Standards", body: "Six pillars · 135 evaluation criteria · scored 0–5." },
  { icon: Award, title: "Certification Programs", body: "Property, Destination, and Airlines certification tracks." },
  { icon: Leaf, title: "Sustainability Benchmarks", body: "Environmental responsibility, resource management, waste reduction, sustainable purchasing, community impact, and responsible tourism practices." },
  { icon: Compass, title: "Digital Transformation", body: "Smart destinations, AI-assisted operations." },
  { icon: Users, title: "Visitor Experience", body: "Independent audits, mystery guest assessments, evidence-based." },
];

const EDITORIALS = [
  {
    kind: "Interview",
    title: "The next decade of luxury hospitality belongs to those who measure trust.",
    author: "L. Renard · Founding Chair",
    img: "https://images.unsplash.com/photo-1551918120-9739cb430c6d?q=80&w=800&auto=format&fit=crop",
    duration: "12 min read",
  },
  {
    kind: "White Paper",
    title: "Government tourism strategy in a post-volatility world.",
    author: "Government Policy Council",
    img: "https://images.unsplash.com/photo-1571171637578-41bc2dd41cd2?q=80&w=800&auto=format&fit=crop",
    duration: "42 pages",
  },
  {
    kind: "Podcast",
    title: "Sustainability isn't a department — it's a discipline.",
    author: "Quality Conversations · Ep. 14",
    img: "https://images.unsplash.com/photo-1453738773917-9c3eff1db985?q=80&w=800&auto=format&fit=crop",
    duration: "38 min",
  },
  {
    kind: "Research",
    title: "Mapping the global quality gap — 2026 benchmarks.",
    author: "WTTQ Research Lab",
    img: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=800&auto=format&fit=crop",
    duration: "Report",
  },
];

const EVENTS = [
  { date: "Apr 16–18, 2026", title: "Annual Alliance Summit", city: "Geneva, Switzerland", kind: "Summit" },
  { date: "May 23, 2026", title: "Executive Roundtable: Asia-Pacific", city: "Singapore", kind: "Roundtable" },
  { date: "Jul 09, 2026", title: "Government Tourism Dialogue", city: "Riyadh, Saudi Arabia", kind: "Dialogue" },
  { date: "Sep 12, 2026", title: "Regional Forum: Latin America", city: "São Paulo, Brazil", kind: "Forum" },
  { date: "Nov 04, 2026", title: "Sustainability Heads Convening", city: "Cape Town, South Africa", kind: "Convening" },
];

const CASE_STUDIES = [
  {
    region: "Maldives",
    challenge: "Inconsistent quality across 130+ island resorts impacting national reputation.",
    solution: "National rollout of WTTQ Property + Destination certifications, with sustainability scoring.",
    result: "+18% repeat visitor rate · 42 properties Gold-certified in 12 months · ESG-aligned operations.",
    img: "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?q=80&w=1200&auto=format&fit=crop",
  },
  {
    region: "Singapore",
    challenge: "Smart-tourism leadership needed evidence-based external validation.",
    solution: "Destination certification with independent audit + digital transformation benchmarks.",
    result: "First nation-state to publish full-stack quality dashboards. Adopted across DMOs in SE Asia.",
    img: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?q=80&w=1200&auto=format&fit=crop",
  },
  {
    region: "Saudi Arabia",
    challenge: "Rapid hospitality build-out across NEOM & Riyadh required unified quality assurance.",
    solution: "WTTQ framework adopted as the national hospitality assurance standard.",
    result: "94 luxury properties under assessment · sustainability scorecards required at planning stage.",
    img: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?q=80&w=1200&auto=format&fit=crop",
  },
];

const COUNCILS = [
  { title: "Executive Board", desc: "Strategic governance · 9 members", count: 9 },
  { title: "Government Council", desc: "Ministers and tourism authorities", count: 24 },
  { title: "Industry Council", desc: "CEOs from hospitality, airlines, cruise", count: 38 },
  { title: "Academic Partners", desc: "Cornell, EHL, Les Roches, NTU Singapore", count: 12 },
  { title: "Innovation Partners", desc: "Technology & sustainability labs", count: 18 },
];

const MEMBERSHIP_TIERS = [
  {
    name: "Corporate Member",
    audience: "Hotel groups, airlines, cruise lines",
    benefits: [
      "Group-wide WTTQ certification eligibility",
      "Executive networking + summit access",
      "Co-branded quality programs",
      "Quarterly industry intelligence briefings",
    ],
    feature: false,
  },
  {
    name: "Government Member",
    audience: "Ministries · tourism boards · DMOs",
    benefits: [
      "Destination certification pathway",
      "Policy advisory + diplomatic representation",
      "National quality dashboards",
      "Cross-border collaboration network",
    ],
    feature: true,
  },
  {
    name: "Strategic Partner",
    audience: "Technology, ESG, hospitality services",
    benefits: [
      "Integration into the Alliance ecosystem",
      "Co-developed certifications",
      "Innovation showcase platform",
      "Joint R&D opportunities",
    ],
    feature: false,
  },
  {
    name: "Advisory Member",
    audience: "Academics · advisors · independent voices",
    benefits: [
      "Editorial platform",
      "Council representation",
      "Curated research access",
      "Honorary recognition",
    ],
    feature: false,
  },
  {
    name: "Media Partner",
    audience: "Publishers · broadcasters · trade press",
    benefits: [
      "Embargoed access to Alliance reports",
      "Editorial collaboration & co-branded series",
      "Press credentials to Alliance summits",
      "Distribution of certification announcements",
    ],
    feature: false,
  },
  {
    name: "Innovative Partner",
    audience: "Startups · scale-ups · sustainability ventures",
    benefits: [
      "Innovation showcase at Alliance events",
      "Pilot programs with member properties",
      "Investor & accelerator introductions",
      "Co-developed pilot certifications",
    ],
    feature: false,
  },
  {
    name: "Executive Board",
    audience: "Industry leaders · founding chairs",
    benefits: [
      "Governance seat on the Alliance board",
      "Direct influence on global quality policy",
      "Private executive convenings",
      "Permanent recognition in Alliance charter",
    ],
    feature: true,
  },
];

const VISION = [
  {
    year: "2029",
    title: "A Leading Voice",
    body: "Positioning the Alliance as the leading voice for quality and excellence in world travel, tourism, hospitality, and government collaboration.",
  },
  {
    year: "2035",
    title: "Universal Recognition",
    body: "Shaping the Future of Quality in Global Tourism.",
  },
  {
    year: "2040",
    title: "The Trusted Network",
    body: "The Alliance becomes the operating system for sustainable, high-quality travel.",
  },
];

const CONTACT_PATHS = [
  { icon: Users, title: "Membership Inquiry", desc: "Become a Corporate, Government, or Strategic member." },
  { icon: Landmark, title: "Government Partnership", desc: "Establish a national quality framework via the Alliance." },
  { icon: Newspaper, title: "Media Relations", desc: "Press, interviews, editorial features." },
  { icon: Sparkles, title: "Strategic Collaboration", desc: "Joint research, innovation, sustainability programs." },
];

// ---------- ANIMATED COUNTER ----------
const Counter = ({ to, suffix = "", duration = 1800 }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const step = (now) => {
            const p = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.round(to * eased));
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      {val.toLocaleString()}
      {suffix}
    </span>
  );
};

// ---------- PAGE ----------
const EMPTY_FORM = {
  business_name: "",
  contact_person: "",
  job_position: "",
  email: "",
  phone: "",
  city: "",
  country: "",
};

const AlliancePage = () => {
  const [activeTimeline, setActiveTimeline] = useState(0);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyTier, setApplyTier] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openApply = (tier) => {
    setApplyTier(tier);
    setForm(EMPTY_FORM);
    setSubmitted(false);
    setApplyOpen(true);
  };

  const closeApply = () => {
    setApplyOpen(false);
    setTimeout(() => {
      setApplyTier(null);
      setSubmitted(false);
      setForm(EMPTY_FORM);
    }, 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ["business_name", "contact_person", "job_position", "email", "phone", "city", "country"];
    for (const k of required) {
      if (!form[k] || !form[k].trim()) {
        toast.error(`Please complete: ${k.replace(/_/g, " ")}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/alliance/applications`, {
        ...form,
        membership_tier: applyTier,
      });
      setSubmitted(true);
      toast.success("Application received. The executive office will be in touch.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const memberCountByType = useMemo(() => {
    const out = {};
    MEMBERS.forEach((m) => {
      out[m.type] = (out[m.type] || 0) + 1;
    });
    return out;
  }, []);

  return (
    <div className="min-h-screen bg-[#06101F] text-white" data-testid="alliance-page">
      <Header />

      {/* 1. HERO */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="alliance-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Alliance" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#06101F]/95 via-[#06101F]/85 to-[#0B132B]/70" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(212,175,55,0.15),transparent_45%)]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <p className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase font-semibold mb-6">
            The Global Alliance for Travel & Tourism Quality
          </p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[1.05] mb-8 max-w-5xl">
            Advancing Excellence Across <span className="text-[#D4AF37]">Global Travel, Tourism & Hospitality.</span>
          </h1>
          <p className="text-lg lg:text-xl text-white/75 max-w-3xl leading-relaxed mb-10">
            A global alliance of industry CEOs, hospitality leaders, tourism authorities, and governments
            committed to elevating quality standards worldwide.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => openApply("Become a Member")}
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B132B] px-8 py-4 text-sm font-semibold tracking-wider uppercase hover:bg-[#E8C456] transition-colors"
              data-testid="alliance-hero-membership"
            >
              Become a Member
              <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href="#membership"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-8 py-4 text-sm font-semibold tracking-wider uppercase hover:bg-white/5 transition-colors"
              data-testid="alliance-hero-explore"
            >
              Explore the Alliance
              <ChevronDown className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* 6. GLOBAL QUALITY FRAMEWORK */}
      <section className="py-24 lg:py-32 bg-gradient-to-b from-[#06101F] to-[#0B132B]" data-testid="alliance-framework">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <p className="text-[#D4AF37] text-xs tracking-[0.35em] uppercase mb-4">Global Quality Framework</p>
            <h2 className="font-heading text-4xl lg:text-5xl font-bold leading-tight">
              The shared language of quality across borders.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {FRAMEWORK.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="relative bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-[#D4AF37]/40 transition-all"
                  data-testid={`framework-${i}`}
                >
                  <div className="text-[10px] tracking-widest text-[#D4AF37] mb-4">0{i + 1}</div>
                  <Icon className="w-7 h-7 text-[#D4AF37] mb-4" />
                  <h3 className="font-heading text-lg font-bold mb-2 leading-tight">{f.title}</h3>
                  <p className="text-xs text-white/60 leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/methodology"
              className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#E8C456] text-sm tracking-wider uppercase font-semibold border-b border-[#D4AF37]/40 hover:border-[#E8C456] pb-1"
              data-testid="framework-cta"
            >
              See the full methodology
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 10. INNOVATION & SUSTAINABILITY */}
      <section className="py-24 lg:py-32 bg-[#0B132B]" data-testid="alliance-innovation">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <p className="text-[#D4AF37] text-xs tracking-[0.35em] uppercase mb-4">Innovation & Sustainability</p>
            <h2 className="font-heading text-4xl lg:text-5xl font-bold leading-tight">
              The Alliance is where industry meets responsibility.
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { icon: Sparkles, title: "Smart Destinations" },
              { icon: TrendingUp, title: "AI in Tourism" },
              { icon: Leaf, title: "Green Hospitality" },
              { icon: ShieldCheck, title: "ESG Programs" },
              { icon: Users, title: "Community Impact" },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  className="aspect-square bg-gradient-to-br from-white/[0.05] to-transparent border border-white/10 rounded-2xl p-6 flex flex-col justify-between hover:border-[#D4AF37]/40 transition"
                >
                  <Icon className="w-8 h-8 text-[#D4AF37]" />
                  <p className="font-heading text-lg font-bold leading-snug">{c.title}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 13. MEMBERSHIP EXPERIENCE */}
      <section id="membership" className="py-24 lg:py-32 bg-[#0B132B]" data-testid="alliance-membership">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <p className="text-[#D4AF37] text-xs tracking-[0.35em] uppercase mb-4">Membership Experience</p>
            <h2 className="font-heading text-4xl lg:text-5xl font-bold leading-tight">
              Designed for the decision-makers shaping global tourism.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {MEMBERSHIP_TIERS.map((t) => (
              <div
                key={t.name}
                className={`relative rounded-2xl p-7 flex flex-col ${
                  t.feature
                    ? "bg-[#D4AF37] text-[#0B132B] border border-[#D4AF37]"
                    : "bg-white/[0.03] border border-white/10 text-white"
                }`}
                data-testid={`tier-${t.name}`}
              >
                {t.feature && (
                  <span className="absolute -top-3 left-7 bg-[#0B132B] text-[#D4AF37] px-3 py-1 text-[10px] tracking-widest uppercase font-bold rounded-full">
                    Most influential
                  </span>
                )}
                <h3 className="font-heading text-2xl font-bold mb-2">{t.name}</h3>
                <p className={`text-xs mb-6 ${t.feature ? "text-[#0B132B]/70" : "text-white/60"}`}>{t.audience}</p>
                <ul className="space-y-3 flex-1 mb-6">
                  {t.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm">
                      <ChevronRight
                        className={`w-4 h-4 mt-0.5 shrink-0 ${t.feature ? "text-[#0B132B]" : "text-[#D4AF37]"}`}
                      />
                      <span className={t.feature ? "text-[#0B132B]" : "text-white/85"}>{b}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => openApply(t.name)}
                  className={`inline-flex items-center justify-center gap-2 text-xs uppercase tracking-widest font-semibold py-3 transition ${
                    t.feature
                      ? "bg-[#0B132B] text-[#D4AF37] hover:bg-[#1a2542]"
                      : "border border-white/30 hover:bg-white/5"
                  }`}
                  data-testid={`tier-apply-${t.name}`}
                >
                  Apply <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 14. VISION 2040 */}
      <section className="py-24 lg:py-32 relative overflow-hidden" data-testid="alliance-vision">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B132B] via-[#06101F] to-[#0B132B]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(212,175,55,0.12),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <p className="text-[#D4AF37] text-xs tracking-[0.35em] uppercase mb-4">Vision 2040</p>
            <h2 className="font-heading text-4xl lg:text-6xl font-bold leading-[1.05] mb-6">
              Building the world&apos;s most trusted quality network for travel & tourism.
            </h2>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute left-0 right-0 top-12 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
              {VISION.map((v, i) => (
                <div key={v.year} className="relative" data-testid={`vision-${v.year}`}>
                  <div className="hidden md:block absolute left-1/2 top-12 -translate-x-1/2 w-3 h-3 rounded-full bg-[#D4AF37] ring-4 ring-[#D4AF37]/20" />
                  <div className={`md:pt-24 ${i === 1 ? "md:text-center" : i === 2 ? "md:text-right" : ""}`}>
                    <div className="text-6xl lg:text-7xl font-heading font-bold text-[#D4AF37]/40 mb-3">{v.year}</div>
                    <h3 className="font-heading text-2xl font-bold mb-3">{v.title}</h3>
                    <p className="text-white/70 leading-relaxed">{v.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 15. CONTACT */}
      <section id="contact" className="py-24 lg:py-32 bg-[#06101F]" data-testid="alliance-contact">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="text-[#D4AF37] text-xs tracking-[0.35em] uppercase mb-4">Connect with the Alliance</p>
              <h2 className="font-heading text-4xl lg:text-5xl font-bold leading-tight mb-6">
                Begin a conversation worthy of your organization.
              </h2>
              <p className="text-white/70 leading-relaxed mb-10">
                Every inquiry is reviewed by the executive office. Expect a personal response within two business days.
              </p>

              <div className="space-y-4">
                {CONTACT_PATHS.map((p) => {
                  const Icon = p.icon;
                  return (
                    <button
                      type="button"
                      key={p.title}
                      onClick={() => openApply(p.title)}
                      className="w-full text-left flex items-start gap-4 p-5 bg-white/[0.03] border border-white/10 rounded-2xl hover:border-[#D4AF37]/40 transition group"
                      data-testid={`contact-path-${p.title}`}
                    >
                      <div className="w-11 h-11 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-[#D4AF37]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-base font-bold group-hover:text-[#D4AF37] transition-colors">
                          {p.title}
                        </h3>
                        <p className="text-xs text-white/60 mt-1">{p.desc}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bg-gradient-to-br from-white/[0.05] to-transparent border border-white/10 rounded-3xl p-10 lg:p-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl pointer-events-none" />
              <Globe2 className="w-10 h-10 text-[#D4AF37] mb-6" />
              <h3 className="font-heading text-2xl font-bold mb-3">Contact Us</h3>

              <div className="space-y-5 text-sm border-t border-white/10 pt-8">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-[#D4AF37]" />
                  <a href="mailto:alliance@wttq.org" className="text-white/85 hover:text-[#D4AF37]">
                    alliance@wttq.org
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-white/85">Executive office · response within 48h</span>
                </div>
                <div className="flex items-center gap-3">
                  <Linkedin className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-white/85">@WTTQAlliance</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => openApply("Executive Briefing")}
                className="mt-10 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B132B] px-7 py-3.5 text-sm font-semibold tracking-wider uppercase hover:bg-[#E8C456] transition"
                data-testid="alliance-contact-cta"
              >
                Request Membership Briefing
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Application Modal */}
      <Dialog open={applyOpen} onOpenChange={(o) => (o ? setApplyOpen(true) : closeApply())}>
        <DialogContent
          className="max-w-2xl bg-[#0B132B] text-white border-white/10 p-0 overflow-hidden"
          data-testid="alliance-apply-modal"
        >
          {submitted ? (
            <div className="p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h3 className="font-heading text-3xl font-bold mb-3">Application received.</h3>
              <p className="text-white/70 max-w-md mx-auto leading-relaxed">
                The Alliance executive office will respond within two business days. Thank you for considering
                membership in the global quality network.
              </p>
              <button
                type="button"
                onClick={closeApply}
                className="mt-8 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B132B] px-6 py-3 text-sm font-semibold tracking-wider uppercase hover:bg-[#E8C456] transition"
                data-testid="alliance-apply-close"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <DialogHeader className="px-8 pt-8 pb-2">
                <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase font-semibold">
                  {applyTier || "Alliance Membership"}
                </p>
                <DialogTitle className="font-heading text-3xl font-bold mt-2 text-white">
                  Apply for the Alliance
                </DialogTitle>
                <DialogDescription className="text-white/60 mt-2">
                  Reviewed personally by the executive office. Response within 48 hours.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="px-8 pb-8 pt-4 space-y-4" data-testid="alliance-apply-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field
                    label="Business Name"
                    name="business_name"
                    value={form.business_name}
                    onChange={(v) => setForm({ ...form, business_name: v })}
                  />
                  <Field
                    label="Contact Person"
                    name="contact_person"
                    value={form.contact_person}
                    onChange={(v) => setForm({ ...form, contact_person: v })}
                  />
                  <Field
                    label="Job Position"
                    name="job_position"
                    value={form.job_position}
                    onChange={(v) => setForm({ ...form, job_position: v })}
                  />
                  <Field
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={(v) => setForm({ ...form, email: v })}
                  />
                  <Field
                    label="Phone"
                    name="phone"
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v })}
                  />
                  <Field
                    label="City"
                    name="city"
                    value={form.city}
                    onChange={(v) => setForm({ ...form, city: v })}
                  />
                  <div className="sm:col-span-2">
                    <Field
                      label="Country"
                      name="country"
                      value={form.country}
                      onChange={(v) => setForm({ ...form, country: v })}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4">
                  <button
                    type="button"
                    onClick={closeApply}
                    className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1"
                  >
                    <X className="w-4 h-4" /> Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B132B] px-7 py-3 text-sm font-semibold tracking-wider uppercase hover:bg-[#E8C456] transition disabled:opacity-50"
                    data-testid="alliance-apply-submit"
                  >
                    {submitting ? "Submitting…" : "Submit Application"}
                    {!submitting && <ArrowRight className="w-4 h-4" />}
                  </button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, name, value, onChange, type = "text" }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-white/60 mb-1.5 block">{label}</span>
    <input
      name={name}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white/[0.04] border border-white/15 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition"
      data-testid={`apply-field-${name}`}
      required
    />
  </label>
);

export default AlliancePage;
